/* eslint-disable react/prop-types */
import useTaskView from '../hooks/useTaskView';
import TaskViewProvider from '../table/TaskViewProvider';
import TaskStatusProvider, { TaskPriorityProvider } from '../table/TaskStatusProvider';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import saveAs from 'file-saver';

import { useLocalStorage } from 'react-use';

import {
  EditingState,
  PagingState,
  IntegratedPaging,
  SortingState,
  IntegratedSorting,
  SearchState,
  IntegratedFiltering,
  SelectionState,
  IntegratedSelection
} from '@devexpress/dx-react-grid';
import { GridExporter } from '@devexpress/dx-react-grid-export';

import {
  Grid,
  Table,
  TableHeaderRow,
  PagingPanel,
  TableColumnResizing,
  DragDropProvider,
  TableColumnReordering,
  SearchPanel,
  Toolbar,
  TableSelection,
  ColumnChooser,
  TableColumnVisibility,
  ExportPanel,
  TableEditColumn
} from '@devexpress/dx-react-grid-material-ui';

import Paper from '@mui/material/Paper';
import { toast } from 'react-hot-toast';
import taskService from '../../../services/TaskService';

import HeaderFilterCell, { HeaderFilterContext } from './customTemplate/HeaderFilterCell';
import { getRowCellValue } from './customTemplate/HeaderFilterCell';
import { Popup, PopupEditing } from './MyTaskPopupEditing';

import KebabCommandCell from '../../UserNew/table/customTemplate/CustomCommandCell';
import { TaskToolbarContext, TaskToolbarRoot } from './customTemplate/CustomToolbarRoot';
import useTaskReminders from '../hooks/useTaskReminders';

// ---------------------------------------------------------------------------
// Unique row ID – MongoDB uses _id
// ---------------------------------------------------------------------------
const getRowId = (row) => row.id || row._id;

// ---------------------------------------------------------------------------
// Clicking a data row opens its task details. Interactive controls such as the
// selection checkbox keep their own behaviour.
// ---------------------------------------------------------------------------
const ClickableTaskRow = ({ row, onOpen, ...restProps }) => {
  const openRow = (event) => {
    if (!row || event.target.closest('button, input, a, [role="checkbox"], [role="button"], [role="menuitem"]')) return;
    onOpen(getRowId(row));
  };

  const handleKeyDown = (event) => {
    if (!row || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    onOpen(getRowId(row));
  };

  return (
    <Table.Row
      {...restProps}
      row={row}
      tabIndex={row ? 0 : undefined}
      onClick={openRow}
      onKeyDown={handleKeyDown}
      aria-label={row ? `Open ${row.title || 'task'} details` : undefined}
      style={{
        ...restProps.style,
        cursor: row ? 'pointer' : restProps.style?.cursor
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// Export helper
// ---------------------------------------------------------------------------
const onSave = (workbook) => {
  workbook.xlsx.writeBuffer().then((buffer) => {
    // eslint-disable-next-line no-undef
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Tasks.xlsx');
  });
};

// ---------------------------------------------------------------------------
// Column definitions per tab
// ---------------------------------------------------------------------------

/** "Assigned to Me" columns – spec §12.1 */
const ASSIGNED_TO_ME_COLUMNS = [
  { name: 'title', title: 'Task Title' },
  { name: 'projectName', title: 'Project' },
  { name: 'type', title: 'Type' },
  { name: 'class', title: 'Class' },
  { name: 'assignedTo', title: 'Assigned To' },
  { name: 'taskViewedByAssignee', title: 'Task Viewed By Assignee' },
  { name: 'taskViewedBySupporter', title: 'Task Viewed By Supporter' },
  { name: 'status', title: 'Status' },
  { name: 'priority', title: 'Priority' },
  { name: 'createdDate', title: 'Created Date' },
  { name: 'dueDate', title: 'Due Date' },
  { name: 'startTime', title: 'Start Time' },
  { name: 'dependencies', title: 'Dependencies' },
  { name: 'description', title: 'Description' },
  { name: 'pendingComment', title: 'Pending Comment' },
  { name: 'supportingStaff', title: 'Supporting Staff' },
  { name: 'location', title: 'Location' },
  { name: 'estimatedHours', title: 'Estimated Hours' },
  { name: 'actualHours', title: 'Actual Hours' },
  { name: 'submittedDate', title: 'Submitted Date' },
  { name: 'createdBy', title: 'Created By' },
  { name: 'updatedAt', title: 'Last Updated' }
];

/** "Created by Me" columns – spec §12.2 */
const CREATED_BY_ME_COLUMNS = [
  { name: 'title', title: 'Task Title' },
  { name: 'projectName', title: 'Project' },
  { name: 'type', title: 'Type' },
  { name: 'class', title: 'Class' },
  { name: 'assignedTo', title: 'Assigned To' },
  { name: 'taskViewedByAssignee', title: 'Task Viewed By Assignee' },
  { name: 'taskViewedBySupporter', title: 'Task Viewed By Supporter' },
  { name: 'status', title: 'Status' },
  { name: 'priority', title: 'Priority' },
  { name: 'createdDate', title: 'Created Date' },
  { name: 'dueDate', title: 'Due Date' },
  { name: 'startTime', title: 'Start Time' },
  { name: 'dependencies', title: 'Dependencies' },
  { name: 'pendingReason', title: 'Pending Reason' },
  { name: 'description', title: 'Description' },
  { name: 'pendingComment', title: 'Pending Comment' },
  { name: 'supportingStaff', title: 'Supporting Staff' },
  { name: 'location', title: 'Location' },
  { name: 'estimatedHours', title: 'Estimated Hours' },
  { name: 'actualHours', title: 'Actual Hours' },
  { name: 'submittedDate', title: 'Submitted Date' },
  { name: 'updatedAt', title: 'Last Updated' }
];

const DEFAULT_COL_WIDTH = 160;
const buildWidths = (cols) => cols.map(({ name }) => ({ columnName: name, width: DEFAULT_COL_WIDTH }));
const buildOrder = (cols) => cols.map(({ name }) => name);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function MyTaskTable({
  tab = 'assignedToMe',
  rows,
  onFilteredRowsChange,
  setRows,
  selection,
  setSelection,
  loading,
  classRecords = [],
  dependencyOptions = [],
  onTaskChanged,
  onAddTask
}) {
  const isCreatedByMe = tab === 'createdByMe';
  const columns = isCreatedByMe ? CREATED_BY_ME_COLUMNS : ASSIGNED_TO_ME_COLUMNS;
  const classOptions = useMemo(
    () => classRecords.map((classRecord) => ({ value: classRecord.value, label: classRecord.name })),
    [classRecords]
  );

  const storageKey = isCreatedByMe ? 'createdByMeColumnWidthsV3' : 'assignedToMeColumnWidthsV3';
  const orderKey = isCreatedByMe ? 'createdByMeColumnOrderV3' : 'assignedToMeColumnOrderV3';

  const [columnWidths, setColumnWidths] = useLocalStorage(storageKey, buildWidths(columns));
  const [columnOrder, setColumnOrder] = useLocalStorage(orderKey, buildOrder(columns));
  const validColumnNames = useMemo(() => columns.map(({ name }) => name), [columns]);
  const resolvedColumnWidths = useMemo(() => {
    const savedWidths = (Array.isArray(columnWidths) ? columnWidths : []).filter(
      ({ columnName, width }) =>
        validColumnNames.includes(columnName) && Number.isFinite(Number(width)) && Number(width) > 0
    );

    return columns.map(({ name }) => ({
      columnName: name,
      width: Number(savedWidths.find(({ columnName }) => columnName === name)?.width) || DEFAULT_COL_WIDTH
    }));
  }, [columnWidths, columns, validColumnNames]);
  const resolvedColumnOrder = useMemo(() => {
    const savedOrder = (Array.isArray(columnOrder) ? columnOrder : []).filter(
      (name, index, order) => validColumnNames.includes(name) && order.indexOf(name) === index
    );
    const missingColumns = columns.map(({ name }) => name).filter((name) => !savedOrder.includes(name));
    return [...savedOrder, ...missingColumns];
  }, [columnOrder, columns, validColumnNames]);

  const [sorting, setSorting] = useState([{ columnName: 'dueDate', direction: 'asc' }]);
  const [hiddenColumnNames, setHiddenColumnNames] = useLocalStorage(
    isCreatedByMe ? 'createdByMeHiddenColumnNames' : 'assignedToMeHiddenColumnNames',
    []
  );

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [pageSizes] = useState([25, 50, 100]);
  const [searchValue, setSearchState] = useState('');

  const [columnFilters, setColumnFilters] = useLocalStorage(
    isCreatedByMe ? 'createdByMeColumnFilters' : 'myTaskColumnFilters',
    {}
  );

  const [taskTabIndex, setTaskTabIndex] = useState(0);
  const [editingRowIds, setEditingRowIds] = useState([]);
  useTaskView(editingRowIds[0], setRows);
  const openTaskDetails = useCallback((rowId) => setEditingRowIds([rowId]), []);
  const startPopupEditing = useCallback(() => setTaskTabIndex(0), []);

  const commitChanges = useCallback(
    async ({ deleted }) => {
      if (!deleted?.length) return;
      // eslint-disable-next-line no-undef
      if (!window.confirm('Are you sure you want to delete this task?')) return;
      try {
        // eslint-disable-next-line no-undef
        await Promise.all(deleted.map((rowId) => taskService.deleteTask(rowId)));
        const deletedIds = deleted.map(String);
        setRows((currentRows) => currentRows.filter((task) => !deletedIds.includes(String(getRowId(task)))));
        onTaskChanged?.();
        toast.success('Task deleted');
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Could not delete task');
      }
    },
    [onTaskChanged, setRows]
  );

  const onUpdateRow = useCallback(
    (updatedRow) => {
      setRows((prevRows) => prevRows.map((r) => (r.id === updatedRow.id ? { ...r, ...updatedRow } : r)));
      onTaskChanged?.();
    },
    [onTaskChanged, setRows]
  );

  // ── Export ─────────────────────────────────────────────────────────────────
  const exporterRef = useRef(null);
  const startExport = useCallback((options) => exporterRef.current.exportGrid(options), [exporterRef]);

  // ── Header-filter rows ─────────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      for (const columnName of Object.keys(columnFilters)) {
        const activeVals = columnFilters[columnName];
        if (activeVals && activeVals.length > 0) {
          const rowVal = getRowCellValue(row, columnName);
          if (!activeVals.includes(rowVal)) return false;
        }
      }
      return true;
    });
  }, [rows, columnFilters]);

  useEffect(() => {
    onFilteredRowsChange?.(filteredRows.length);
  }, [filteredRows, onFilteredRowsChange]);

  const { isSendingReminders, sendAllReminders, sendSelectedReminders } = useTaskReminders({
    filteredRows,
    selection,
    taskSource: 'myTask'
  });

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '3rem', color: '#9e9e9e' }}>Loading tasks...</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: '4px', fontSize: '0.85rem', color: '#777' }}>
        {filteredRows.length} task{filteredRows.length !== 1 ? 's' : ''}
        {selection.length > 0 && ` · ${selection.length} selected`}
      </div>

      <TaskToolbarContext.Provider
        value={{
          showAddTask: true,
          showSend: isCreatedByMe,
          selectionCount: selection.length,
          isSending: isSendingReminders,
          isAddTaskDisabled: loading,
          onAddTask,
          onSendAll: sendAllReminders,
          onSendSelected: sendSelectedReminders
        }}
      >
        <HeaderFilterContext.Provider value={{ rows, columnFilters, onFilterChange: setColumnFilters }}>
          <Paper>
            <Grid rows={filteredRows} columns={columns} getRowId={getRowId}>
              <EditingState
                editingRowIds={editingRowIds}
                onEditingRowIdsChange={setEditingRowIds}
                onCommitChanges={commitChanges}
                columnExtensions={[
                  { columnName: 'taskViewedByAssignee', editingEnabled: false },
                  { columnName: 'taskViewedBySupporter', editingEnabled: false }
                ]}
              />

              <TaskViewProvider />
              <TaskStatusProvider />
              <TaskPriorityProvider />

              <PagingState
                currentPage={currentPage}
                onCurrentPageChange={setCurrentPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
              />
              <SelectionState selection={selection} onSelectionChange={setSelection} />
              <IntegratedSelection />
              <DragDropProvider />
              <SortingState sorting={sorting} onSortingChange={setSorting} />
              <IntegratedSorting />
              <SearchState value={searchValue} onValueChange={setSearchState} />
              <IntegratedFiltering />
              <IntegratedPaging />

              <Table rowComponent={(props) => <ClickableTaskRow {...props} onOpen={openTaskDetails} />} />
              <TableColumnResizing columnWidths={resolvedColumnWidths} onColumnWidthsChange={setColumnWidths} />
              <TableColumnReordering order={resolvedColumnOrder} onOrderChange={setColumnOrder} />
              <TableHeaderRow showSortingControls cellComponent={HeaderFilterCell} />
              <TableSelection showSelectAll />
              <PagingPanel pageSizes={pageSizes} />
              {isCreatedByMe && (
                <TableEditColumn
                  width={90}
                  showEditCommand
                  showDeleteCommand
                  cellComponent={(props) => <KebabCommandCell {...props} onPopupEditStart={startPopupEditing} />}
                />
              )}
              <PopupEditing
                popupComponent={Popup}
                taskTabIndex={taskTabIndex}
                setTaskTabIndex={setTaskTabIndex}
                onUpdateRow={onUpdateRow}
                tab={tab}
                classOptions={classOptions}
                dependencyOptions={dependencyOptions}
              />
              <TableColumnVisibility
                hiddenColumnNames={hiddenColumnNames}
                onHiddenColumnNamesChange={setHiddenColumnNames}
              />
              <Toolbar rootComponent={TaskToolbarRoot} />
              <SearchPanel />
              <ColumnChooser />
              <ExportPanel startExport={startExport} />
            </Grid>

            <GridExporter
              ref={exporterRef}
              columns={columns}
              rows={filteredRows}
              selection={selection}
              onSave={onSave}
              getRowId={getRowId}
              hiddenColumnNames={hiddenColumnNames}
            />
          </Paper>
        </HeaderFilterContext.Provider>
      </TaskToolbarContext.Provider>
    </div>
  );
}
